/** Style Space v2 sub-routes (decision #3: Outfit / Capsule pages are real routes, not `?look=`). */
import { STYLE_SPACE_ROUTE } from '@/router/routes'
import { guessFairStyleSpaceCategory } from '@/widgets/style-space/fair/fair-style-space-outfit-guests.data'

export const FAIR_STYLE_SPACE_V2_OUTFIT_ROUTE = `${STYLE_SPACE_ROUTE}/outfits/:outfitId(\\d+)`
export const FAIR_STYLE_SPACE_V2_CAPSULE_ROUTE = `${STYLE_SPACE_ROUTE}/capsules/:capsuleId(\\d+)`

export function buildFairStyleSpaceV2OutfitLocation(outfitId: number) {
  return { path: `${STYLE_SPACE_ROUTE}/outfits/${outfitId}` }
}

export function buildFairStyleSpaceV2CapsuleLocation(capsuleId: number) {
  return { path: `${STYLE_SPACE_ROUTE}/capsules/${capsuleId}` }
}

/** `?v1=1` escapes v2 back to the v1 page on the same route (§7: no AIRA canvas design yet). */
export const FAIR_STYLE_SPACE_V1_ESCAPE_QUERY = 'v1'

/** FE-012 — `?style_item=<wardrobe item id>` drops that piece onto the v1 canvas. */
export const FAIR_STYLE_SPACE_V1_STYLE_ITEM_QUERY = 'style_item'

/**
 * FE-019 — `?style_product=<product id>` does the same for a catalogue pick the
 * user does **not** own. The piece rides along in history state (below) and lives
 * only in this canvas session: nothing is written to the wardrobe.
 */
export const FAIR_STYLE_SPACE_V1_STYLE_PRODUCT_QUERY = 'style_product'

/**
 * FE-081 — `?edit_look=<outfit id>` открывает образ В РЕДАКТОРЕ: вещи образа по
 * слотам, палитра — вещи этого образа (`available-items`). Save = PATCH.
 * Просмотр сохранённого образа — его собственная страница `/style-space/outfits/:id`;
 * старый `?look=` в v2 не существует (он молча становился редактором).
 */
export const FAIR_STYLE_SPACE_V1_EDIT_LOOK_QUERY = 'edit_look'

/** History-state key carrying what the canvas needs to draw the guest piece. */
export const FAIR_STYLE_SPACE_V1_CANVAS_GUEST_STATE = 'fairStyleSpaceCanvasGuest'

export interface FairStyleSpaceV1CanvasGuest {
  productId: number
  variantId: number | null
  imageSrc: string
  category: string
  typeId?: number
  name?: string
  brand?: string | null
  /**
   * FE-020 (v1 only) — ask AIRA to build a look around this piece on arrival.
   * The v2 canvas never generates on arrival: the piece lands pinned and the shopper
   * starts the deal (`.cursor/PLAYGROUND_V2_FLOW_SPEC.md` §2). Kept for the v1 page.
   */
  generate?: boolean
}

/** The redesigned canvas has a route of its own — no more `?v1=1` escape. */
export const FAIR_STYLE_SPACE_V2_PLAYGROUND_ROUTE = `${STYLE_SPACE_ROUTE}/playground`

/** `?source=wardrobe|mixed|catalog` — the toolbar's `From: …` preset on arrival. */
export const FAIR_STYLE_SPACE_V2_SOURCE_QUERY = 'source'

/**
 * `?draft=1` + history state — an UNSAVED look onto the canvas (spec §2: the product page's
 * look block, "Edit with AIRA" / "Show more outfits"). Nothing is written until `Save outfit`.
 * `mode: 'variations'` asks the stylist for three takes on it straight away.
 */
export const FAIR_STYLE_SPACE_V2_DRAFT_QUERY = 'draft'
export const FAIR_STYLE_SPACE_V2_CANVAS_DRAFT_STATE = 'fairStyleSpaceCanvasDraft'

export interface FairStyleSpaceV2CanvasDraft {
  title: string
  items: FairStyleSpaceV1CanvasGuest[]
  mode: 'edit' | 'variations'
}

export function buildFairStyleSpaceDraftLocation(draft: FairStyleSpaceV2CanvasDraft) {
  return {
    path: FAIR_STYLE_SPACE_V2_PLAYGROUND_ROUTE,
    query: { [FAIR_STYLE_SPACE_V2_DRAFT_QUERY]: '1' },
    state: { [FAIR_STYLE_SPACE_V2_CANVAS_DRAFT_STATE]: { ...draft, items: draft.items.map((item) => ({ ...item })) } },
  }
}

export function readFairStyleSpaceV2CanvasDraft(state: unknown): FairStyleSpaceV2CanvasDraft | null {
  const raw = (state as Record<string, unknown> | null)?.[FAIR_STYLE_SPACE_V2_CANVAS_DRAFT_STATE]
  if (!raw || typeof raw !== 'object') return null
  const draft = raw as Partial<FairStyleSpaceV2CanvasDraft>
  if (!Array.isArray(draft.items)) return null
  const items = draft.items.filter(
    (item): item is FairStyleSpaceV1CanvasGuest =>
      Boolean(item && typeof item === 'object' && item.productId != null && item.imageSrc && item.category),
  )
  if (!items.length) return null
  return {
    title: typeof draft.title === 'string' ? draft.title : '',
    items,
    mode: draft.mode === 'variations' ? 'variations' : 'edit',
  }
}

/**
 * The playground: `/style-space/playground`. `edit_look` opens a saved outfit in the editor,
 * `style_item` starts a fresh canvas with one wardrobe piece on it, `style_product`
 * (plus the piece in history state) with a catalogue guest.
 */
export function buildFairStyleSpacePlaygroundLocation(
  options: {
    /** FE-081 — открыть этот образ в редакторе (канвас + палитра образа). */
    editOutfitId?: number
    itemId?: string
    /** Catalogue pick to place on the canvas without adding it to the wardrobe. */
    guest?: FairStyleSpaceV1CanvasGuest
    /** `From: …` preset — K7 "Mix with my wardrobe" opens the canvas on the wardrobe. */
    itemSource?: 'wardrobe' | 'mixed' | 'catalog'
  } = {},
) {
  const { editOutfitId, itemId, guest, itemSource } = options
  return {
    path: FAIR_STYLE_SPACE_V2_PLAYGROUND_ROUTE,
    query: {
      ...(itemSource ? { [FAIR_STYLE_SPACE_V2_SOURCE_QUERY]: itemSource } : {}),
      ...(editOutfitId === undefined
        ? {}
        : { [FAIR_STYLE_SPACE_V1_EDIT_LOOK_QUERY]: String(editOutfitId) }),
      ...(itemId ? { [FAIR_STYLE_SPACE_V1_STYLE_ITEM_QUERY]: itemId } : {}),
      ...(guest ? { [FAIR_STYLE_SPACE_V1_STYLE_PRODUCT_QUERY]: String(guest.productId) } : {}),
    },
    ...(guest ? { state: { [FAIR_STYLE_SPACE_V1_CANVAS_GUEST_STATE]: { ...guest } } } : {}),
  }
}

/**
 * «Style it» в меню карточки товара — FE-065.
 *
 * Пункт есть на каждой сетке и полке, и везде он толкал
 * `/style-space?productId=…&variantId=…`. Страница такие параметры НЕ ЧИТАЕТ:
 * она ждёт `style_product` плюс саму вещь в `history.state`. Поэтому «Style it»
 * открывал пустой гардероб, и товар терялся.
 *
 * Здесь собирается тот же переход, что давно делает кнопка «Create a look with
 * this …» на странице товара: вещь кладётся на холст гостем (в гардероб её не
 * пишут) и закрепляется — образ вокруг неё собирает пользователь (чип повода /
 * Surprise me), автогенерации при входе нет.
 *
 * Категорию угадываем по названию: у карточки сетки нет ни `type_id`, ни
 * хлебных крошек. Промах не ломает холст — сгенерированный образ несёт
 * настоящий слот, — но передать нечего лучше.
 */
export function buildFairStyleSpaceLookFromCardLocation(card: {
  productId: number
  variantId?: number | null
  title?: string | null
  brand?: string | null
  imageUrl?: string | null
}) {
  // без картинки гость не рисуется (`readFairStyleSpaceV1CanvasGuest`) —
  // открываем playground пустым, а не с переходом, который молча ничего не даст
  if (!card.imageUrl) return { path: STYLE_SPACE_ROUTE }
  return buildFairStyleSpacePlaygroundLocation({
    guest: {
      productId: card.productId,
      variantId: card.variantId ?? null,
      imageSrc: card.imageUrl,
      category: guessFairStyleSpaceCategory(card.title),
      name: card.title ?? '',
      brand: card.brand ?? null,
    },
  })
}

/**
 * Read the guest back on the v1 page. The query alone is not enough to draw the
 * piece (image, category), so a reload that loses history state simply skips it.
 */
export function readFairStyleSpaceV1CanvasGuest(
  state: unknown,
  productId: string | null,
): FairStyleSpaceV1CanvasGuest | null {
  if (!productId) return null
  const raw = (state as Record<string, unknown> | null)?.[FAIR_STYLE_SPACE_V1_CANVAS_GUEST_STATE]
  if (!raw || typeof raw !== 'object') return null
  const guest = raw as Partial<FairStyleSpaceV1CanvasGuest>
  if (String(guest.productId ?? '') !== productId) return null
  if (!guest.imageSrc || !guest.category) return null
  return {
    productId: Number(guest.productId),
    variantId: guest.variantId ?? null,
    imageSrc: guest.imageSrc,
    category: guest.category,
    typeId: guest.typeId,
    name: guest.name,
    brand: guest.brand ?? null,
    generate: guest.generate === true,
  }
}

/**
 * FE-081 — «Edit» на странице аутфита: тот же редактор, что открывает v1
 * изнутри, но по прямой ссылке.
 */
export function buildFairStyleSpaceV2EditOutfitLocation(outfitId: number) {
  return buildFairStyleSpacePlaygroundLocation({ editOutfitId: outfitId })
}
